import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { ApiError, US_REGIONS, type ConsentKind, type LifeStage, type PatientContactUpdate, type PatientDetails, type Sex } from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, Field, Text } from "@ferrivia/ui";
import { notifyPatientStale } from "../../../../hooks/patientBus";
import { useCustomerDetails } from "../../../../hooks/useCustomerDetails";
import s from "./DetailsPane.module.css";

export interface DetailsPaneProps {
  customerId: string;
}

// Matches clinic.patient_contact_updated's `fields` enum in the analytics
// catalog — see apps/api/src/modules/patients/routes.ts, which the same
// mapping is duplicated from (api-contract has no shared place for it).
const CONTACT_FIELD_NAMES: Record<keyof PatientContactUpdate, "preferred_name" | "email" | "phone"> = {
  preferredName: "preferred_name",
  email: "email",
  phone: "phone",
};

function legalName(details: Pick<PatientDetails, "givenName" | "familyName">, fallback: string): string {
  const full = [details.givenName, details.familyName].filter(Boolean).join(" ");
  return full || fallback;
}

interface ContactSectionProps {
  details: PatientDetails;
  onSaved: (details: PatientDetails) => void;
}

function ContactSection({ details, onSaved }: ContactSectionProps) {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("clinic");

  const baselinePreferredName = details.preferredName ?? details.givenName ?? "";
  const baselineEmail = details.email ?? "";
  const baselinePhone = details.phone ?? "";

  const [preferredName, setPreferredName] = useState(baselinePreferredName);
  const [email, setEmail] = useState(baselineEmail);
  const [phone, setPhone] = useState(baselinePhone);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const dirty = preferredName !== baselinePreferredName || email !== baselineEmail || phone !== baselinePhone;
  const canSave = dirty && !saving;

  async function handleSave() {
    const update: PatientContactUpdate = {};
    if (preferredName !== baselinePreferredName) {
      update.preferredName = preferredName.trim().length > 0 ? preferredName.trim() : null;
    }
    if (email !== baselineEmail) {
      update.email = email.trim().length > 0 ? email.trim() : null;
    }
    if (phone !== baselinePhone) {
      update.phone = phone.trim().length > 0 ? phone.trim() : null;
    }
    if (Object.keys(update).length === 0) {
      return;
    }

    setSaving(true);
    setFailed(false);
    setEmailError(null);
    try {
      const updated = await api.patients.updateContact(details.id, update);
      onSaved(updated);
      notifyPatientStale(details.id);
      tracker.track("clinic.patient_contact_updated", {
        patient_id: details.id,
        fields: (Object.keys(update) as (keyof PatientContactUpdate)[]).map((key) => CONTACT_FIELD_NAMES[key]),
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_in_use") {
        setEmailError(t("detailsPane.contact.emailInUse", "This email is already used by another account."));
      } else {
        setFailed(true);
      }
      tracker.track("clinic.patient_contact_update_failed", {
        patient_id: details.id,
        reason: err instanceof ApiError ? err.code : "network",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={s.section} aria-labelledby="details-contact-heading">
      <Text as="h2" id="details-contact-heading" size="lg" weight="bold" className={s.heading}>
        {t("detailsPane.contact.title", "Contact")}
      </Text>
      <div className={s.fields}>
        <Field
          label={t("detailsPane.contact.preferredName", "Preferred name")}
          value={preferredName}
          onChange={(e) => setPreferredName(e.target.value)}
          disabled={saving}
        />
        <Field
          label={t("detailsPane.contact.email", "Email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={saving}
          errorText={emailError ?? undefined}
        />
        <Field
          label={t("detailsPane.contact.phone", "Phone")}
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={saving}
        />
      </div>
      {failed && (
        <Text as="p" size="sm" className={s.error}>
          {t("detailsPane.contact.saveFailed", "Couldn't save. Try again.")}
        </Text>
      )}
      <Button variant="primary" onClick={handleSave} disabled={!canSave}>
        {t("detailsPane.contact.save", "Save")}
      </Button>
    </section>
  );
}

function IdentitySection({ details }: { details: PatientDetails }) {
  const { t } = useTranslation("clinic");
  const { date } = useFormatters();

  return (
    <section className={s.section} aria-labelledby="details-identity-heading">
      <Text as="h2" id="details-identity-heading" size="lg" weight="bold" className={s.heading}>
        {t("detailsPane.identity.title", "Identity")}
      </Text>
      <dl className={s.dl}>
        <div className={s.row}>
          <dt className={s.dt}>{t("detailsPane.identity.legalName", "Legal name")}</dt>
          <dd className={s.dd}>{legalName(details, t("detailsPane.identity.legalNameUnknown", "Not available"))}</dd>
        </div>
        <div className={s.row}>
          <dt className={s.dt}>{t("detailsPane.identity.verifiedAt", "Verified")}</dt>
          <dd className={s.dd}>
            {details.identityVerifiedAt
              ? date.format(new Date(details.identityVerifiedAt))
              : t("detailsPane.identity.notVerified", "Not verified")}
          </dd>
        </div>
      </dl>
    </section>
  );
}

interface ProfileSectionProps {
  details: PatientDetails;
  onReset: (details: PatientDetails) => void;
}

// Dev/staging only: the API route itself doesn't exist in production
// (apps/api/src/modules/patients/routes.ts), so this is purely a UI
// affordance, not the security boundary.
const CAN_RESET_ONBOARDING = import.meta.env.VITE_APP_ENV !== "prod";

function ProfileSection({ details, onReset }: ProfileSectionProps) {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("clinic");
  const profile = details.profile;
  const [resetting, setResetting] = useState(false);
  const [resetFailed, setResetFailed] = useState(false);

  async function handleResetOnboarding() {
    setResetting(true);
    setResetFailed(false);
    try {
      const updated = await api.patients.resetOnboarding(details.id);
      onReset(updated);
      tracker.track("clinic.patient_onboarding_reset", { patient_id: details.id });
    } catch {
      setResetFailed(true);
    } finally {
      setResetting(false);
    }
  }

  const sexLabels: Record<Sex, string> = {
    female: t("detailsPane.sex.female", "Female"),
    male: t("detailsPane.sex.male", "Male"),
  };

  const lifeStageLabels: Record<LifeStage, string> = {
    menstruating: t("detailsPane.lifeStage.menstruating", "Has periods"),
    planning_pregnancy: t("detailsPane.lifeStage.planning_pregnancy", "Planning pregnancy"),
    trying_to_conceive: t("detailsPane.lifeStage.trying_to_conceive", "Trying to conceive"),
    fertility_treatment: t("detailsPane.lifeStage.fertility_treatment", "Fertility treatment"),
    pregnant: t("detailsPane.lifeStage.pregnant", "Pregnant"),
    postpartum: t("detailsPane.lifeStage.postpartum", "Postpartum"),
    perimenopause: t("detailsPane.lifeStage.perimenopause", "Perimenopause"),
    menopause: t("detailsPane.lifeStage.menopause", "Menopause"),
    postmenopause: t("detailsPane.lifeStage.postmenopause", "Postmenopause"),
  };

  return (
    <section className={s.section} aria-labelledby="details-profile-heading">
      <Text as="h2" id="details-profile-heading" size="lg" weight="bold" className={s.heading}>
        {t("detailsPane.profile.title", "Profile")}
      </Text>
      {profile ? (
        <>
          <dl className={s.dl}>
            <div className={s.row}>
              <dt className={s.dt}>{t("detailsPane.profile.birthYear", "Birth year")}</dt>
              <dd className={s.dd}>{profile.birthYear ?? t("detailsPane.profile.notAnswered", "Not answered")}</dd>
            </div>
            <div className={s.row}>
              <dt className={s.dt}>{t("detailsPane.profile.sex", "Sex")}</dt>
              <dd className={s.dd}>
                {profile.sex ? sexLabels[profile.sex] : t("detailsPane.profile.notAnswered", "Not answered")}
              </dd>
            </div>
            <div className={s.row}>
              <dt className={s.dt}>{t("detailsPane.profile.country", "Country")}</dt>
              <dd className={s.dd}>{profile.country ?? t("detailsPane.profile.notAnswered", "Not answered")}</dd>
            </div>
            {profile.region && !profile.city ? null : (
              <div className={s.row}>
                <dt className={s.dt}>{t("detailsPane.profile.city", "City")}</dt>
                <dd className={s.dd}>{profile.city ?? t("detailsPane.profile.notAnswered", "Not answered")}</dd>
              </div>
            )}
            {profile.region ? (
              <div className={s.row}>
                <dt className={s.dt}>{t("detailsPane.profile.region", "State")}</dt>
                <dd className={s.dd}>
                  {US_REGIONS.find((region) => region.code === profile.region)?.name ?? profile.region}
                </dd>
              </div>
            ) : null}
            <div className={s.row}>
              <dt className={s.dt}>{t("detailsPane.profile.completedLabel", "Onboarding status")}</dt>
              <dd className={s.dd}>
                {CAN_RESET_ONBOARDING && profile.completedAt ? (
                  <button
                    type="button"
                    className={s.statusReset}
                    onClick={handleResetOnboarding}
                    disabled={resetting}
                    title={t(
                      "detailsPane.profile.resetOnboardingHint",
                      "Click to send this customer back to onboarding (dev/staging only). Their answers stay saved.",
                    )}
                  >
                    {t("detailsPane.profile.completed", "Completed")}
                  </button>
                ) : profile.completedAt ? (
                  t("detailsPane.profile.completed", "Completed")
                ) : (
                  t("detailsPane.profile.notCompleted", "Not completed")
                )}
                {resetFailed && (
                  <Text as="p" size="sm" className={s.error}>
                    {t("detailsPane.profile.resetOnboardingFailed", "Couldn't reset onboarding. Try again.")}
                  </Text>
                )}
              </dd>
            </div>
          </dl>
          <div className={s.lifeStages}>
            <span className={s.dt}>{t("detailsPane.profile.lifeStages", "Life stages")}</span>
            {/* An empty array is a real answer ("none of these apply") — distinct
                from lifeStagesAnsweredAt === null, which means the question was
                never reached. Rendering them the same would turn "answered
                none" into "unanswered", which isn't true. */}
            {profile.lifeStagesAnsweredAt === null ? (
              <Text as="span" size="sm" className={s.helper}>
                {t("detailsPane.profile.lifeStagesUnanswered", "Not answered yet")}
              </Text>
            ) : profile.lifeStages.length === 0 ? (
              <Text as="span" size="sm" className={s.helper}>
                {t("detailsPane.profile.lifeStagesNone", "None selected")}
              </Text>
            ) : (
              <ul className={s.chipList}>
                {profile.lifeStages.map((stage) => (
                  <li key={stage}>{lifeStageLabels[stage]}</li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <Text as="p" size="sm" className={s.helper}>
          {t("detailsPane.profile.noAnswers", "No profile answers yet.")}
        </Text>
      )}
    </section>
  );
}

function ConsentsSection({ details }: { details: PatientDetails }) {
  const { t } = useTranslation("clinic");
  const { date } = useFormatters();

  const consentKindLabels: Record<ConsentKind, string> = {
    terms_and_privacy: t("detailsPane.consentKind.terms_and_privacy", "Terms and privacy"),
    health_data_processing: t("detailsPane.consentKind.health_data_processing", "Health data processing"),
    lab_partner_result_upload: t("detailsPane.consentKind.lab_partner_result_upload", "Lab partner result upload"),
    health_questionnaire_collection: t(
      "detailsPane.consentKind.health_questionnaire_collection",
      "Health questionnaire collection",
    ),
  };

  return (
    <section className={s.section} aria-labelledby="details-consents-heading">
      <Text as="h2" id="details-consents-heading" size="lg" weight="bold" className={s.heading}>
        {t("detailsPane.consents.title", "Consents")}
      </Text>
      {details.consents.length === 0 ? (
        <Text as="p" size="sm" className={s.helper}>
          {t("detailsPane.consents.none", "No consents recorded.")}
        </Text>
      ) : (
        <ul className={s.consentList}>
          {details.consents.map((consent) => (
            <li key={consent.kind} className={s.consentRow}>
              <span className={s.consentCheckCol} aria-hidden="true">
                {consent.granted && <Check size={16} className={s.consentCheck} />}
              </span>
              <Text as="span" className={s.consentName}>
                {consentKindLabels[consent.kind]}
              </Text>
              <Text
                as="span"
                size="sm"
                className={s.consentDate}
                aria-label={
                  consent.granted
                    ? t("detailsPane.consents.acceptedAria", {
                        date: date.format(new Date(consent.recordedAt)),
                        defaultValue: "Accepted {{date}}",
                      })
                    : t("detailsPane.consents.notAcceptedAria", "Not accepted")
                }
              >
                {consent.granted ? date.format(new Date(consent.recordedAt)) : null}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function DetailsPane({ customerId }: DetailsPaneProps) {
  const tracker = useTracker();
  const { t } = useTranslation("clinic");
  const [state, setDetails] = useCustomerDetails(customerId);

  const loadedId = state.status === "success" ? state.details.id : null;
  useEffect(() => {
    if (loadedId) {
      tracker.track("clinic.patient_details_viewed", { patient_id: loadedId });
    }
    // Fire once per successful load (this pane is remounted per customerId
    // by its caller), not on every tracker identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  if (state.status === "loading") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("detailsPane.loading", "Loading details…")}
      </Text>
    );
  }

  if (state.status === "error") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("detailsPane.error", "Couldn't load details.")}
      </Text>
    );
  }

  return (
    <div className={s.root}>
      <ContactSection details={state.details} onSaved={setDetails} />
      <IdentitySection details={state.details} />
      <ProfileSection details={state.details} onReset={setDetails} />
      <ConsentsSection details={state.details} />
    </div>
  );
}
